/**
 * 提示分级 —— 对应技术设计 §7.1 `modeDefaults.hintLevel` 与 §14.1「渐进提示」。
 *
 * 【§22 红线 6】「学习模式要给足引导，考核模式要能隐藏引导」。
 * 落点是这里的 `hintLevel`：
 *   2（learn）    目标 + 具体做法 + 端口常显
 *   1（practice） 只给目标，卡住一段时间后才升级到做法
 *   0（exam）     不给提示，只显示步骤标题
 *
 * 「卡住才升级」而不是一上来就给全，是 §14.1「渐进提示」的要求：
 * 直接给答案会让学生跳过思考，而这套系统评的正是思辨与探究（能力维度 3/5）。
 */

import type { LabMode } from "../data/schemas.js";
import type { StepDefinition } from "./step-checker.js";

export type HintLevel = 0 | 1 | 2;

export const MODE_HINT_LEVEL: Record<LabMode, HintLevel> = {
  learn: 2,
  practice: 1,
  exam: 0,
};

/** practice 档卡多久才升级提示。太短等于白给，太长学生已经放弃。 */
const STUCK_ESCALATE_MS = 25_000;

export interface HintRequest {
  step: StepDefinition;
  mode: LabMode;
  /** 本步已经耗时多久 */
  elapsedMs: number;
  /** 本步已经判错几次 */
  errorCount: number;
  /** 实验包 modeDefaults 的覆盖值 */
  overrideLevel?: HintLevel;
}

export interface Hint {
  /** 始终给：当前步骤标题 */
  title: string;
  /** 目标，一句话（level ≥ 1） */
  goal?: string;
  /** 具体做法（level ≥ 2，或 practice 卡住/错过之后） */
  detail?: string;
  /** 当前实际生效的档位，UI 据此决定要不要显示"提示"角标 */
  level: HintLevel;
}

function effectiveLevel(request: HintRequest): HintLevel {
  const base = request.overrideLevel ?? MODE_HINT_LEVEL[request.mode];
  if (base >= 2) return 2;
  // exam 永不升级：考核模式的引导必须真的关掉，不能因为学生卡住就漏出来
  if (base === 0) return 0;
  const stuck = request.elapsedMs >= STUCK_ESCALATE_MS || request.errorCount > 0;
  return stuck ? 2 : 1;
}

export function buildHint(request: HintRequest): Hint {
  const level = effectiveLevel(request);
  const { step } = request;

  const hint: Hint = { title: step.title, level };
  if (level >= 1 && step.goal !== undefined) hint.goal = step.goal;
  if (level >= 2) {
    // 错得越多给得越具体（§8「重复错误升级解释深度」）
    const index = Math.min(request.errorCount, (step.hints?.length ?? 1) - 1);
    const detail = step.hints?.[Math.max(0, index)];
    if (detail !== undefined) hint.detail = detail;
  }
  return hint;
}
